import { ApolloError, gql, useMutation } from '@apollo/client';
import {
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  FormControl,
} from '@apollo/orbit';
import { useFormik } from 'formik';
import React, { useCallback, useState } from 'react';
import * as Yup from 'yup';

import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { ExcludedOperationsTable } from '../../checksConfigPage/excludedOperationsTable/ExcludedOperationsTable';
import { removeTypenameFromExcludedOperationNames } from '../../checksConfigPage/transforms';
import { SharedCheckConfigCardProps } from '../common/SharedCheckConfigCardProps';
import { UseGraphSettingsButton } from '../common/UseGraphSettingsButton';
import { VariantCheckConfigFragment } from '../VariantCheckConfigFragment';

const variantCheckConfigExcludedOperationsMutation = gql`
  mutation VariantCheckConfigExcludedOperationsMutation(
    $graphId: ID!
    $graphVariant: String!
    $appendGraphSettings: Boolean!
    $excludedOperationNames: [OperationNameFilterInput!]
  ) {
    service(id: $graphId) {
      variant(name: $graphVariant) {
        updateCheckConfigurationExcludedOperations(
          appendGraphSettings: $appendGraphSettings
          excludedOperationNames: $excludedOperationNames
        ) {
          id
          ...VariantCheckConfigFragment
        }
      }
    }
  }
  ${VariantCheckConfigFragment}
`;

/**
 * Schema for the form
 */
const validationSchema = Yup.object().shape({
  appendGraphSettings: Yup.boolean().required(),
  excludedOperationNames: Yup.array(
    Yup.object({
      name: Yup.string(),
    }),
  ).nullable(),
});

/**
 * Types inferred from `validationSchema`
 */
type FormValues = Yup.InferType<typeof validationSchema>;

type Props = SharedCheckConfigCardProps;

export const ExcludedOperationsCard = ({
  graphRef,
  graphCheckConfig,
  variantCheckConfig,
  roles,
}: Props) => {
  const [updateConfigError, setUpdateConfigError] =
    useState<ApolloError | null>(null);

  const [updateCheckConfig] = useMutation<
    GraphQLTypes.VariantCheckConfigExcludedOperationsMutation,
    GraphQLTypes.VariantCheckConfigExcludedOperationsMutationVariables
  >(variantCheckConfigExcludedOperationsMutation, {
    onError: setUpdateConfigError,
  });

  const appendGraphSettings =
    variantCheckConfig.excludedOperationsConfig.appendGraphSettings;

  const { values, setFieldValue, submitForm } = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      appendGraphSettings,
      excludedOperationNames: removeTypenameFromExcludedOperationNames(
        variantCheckConfig.excludedOperationsConfig.excludedOperationNames,
      ),
    },
    onSubmit: async (formValues) => {
      await updateCheckConfig({
        variables: {
          graphId: graphRef.graphId,
          graphVariant: graphRef.graphVariant,
          appendGraphSettings: formValues.appendGraphSettings,
          excludedOperationNames: formValues.excludedOperationNames,
        },
      });
    },
  });

  const clearErrorsAndSubmit = useCallback(() => {
    setUpdateConfigError(null);
    submitForm();
  }, [setUpdateConfigError, submitForm]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Excluded operations</CardTitle>
        <CardDescription>
          Any operation names sent with these names will not be checked against
          changes to the schema. This is useful to filter out known and expected
          operations that raise flags during schema checks.
        </CardDescription>

        <FormControl>
          <UseGraphSettingsButton
            label="Append Graph Settings"
            tooltipContent={
              values.appendGraphSettings
                ? 'Additionally exclude operations configured by this graph.'
                : undefined
            }
            isSelected={values.appendGraphSettings}
            disabled={!roles.canWriteCheckConfiguration}
            onChange={(value) => {
              setFieldValue('appendGraphSettings', value);
              clearErrorsAndSubmit();
            }}
          />
        </FormControl>
      </CardHeader>
      <CardBody>
        <ExcludedOperationsTable
          graphId={graphRef.graphId}
          excludedOperationNames={values.excludedOperationNames}
          appendedExcludedOperationNames={
            values.appendGraphSettings
              ? removeTypenameFromExcludedOperationNames(
                  graphCheckConfig.excludedOperationNames ?? [],
                )
              : undefined
          }
          onNewExcludedOperationName={(name: string) => {
            const newOperations = [];
            if (values.excludedOperationNames?.length) {
              newOperations.push(...values.excludedOperationNames);
            }
            newOperations.push({ name });
            setFieldValue('excludedOperationNames', newOperations);
            clearErrorsAndSubmit();
          }}
          onExcludedOperationNameRemove={(filterName: string) => {
            setFieldValue(
              'excludedOperationNames',
              values.excludedOperationNames?.filter(
                ({ name }) => name !== filterName,
              ),
            );
            clearErrorsAndSubmit();
          }}
        />

        {/* Form update error message */}
        {updateConfigError && <ErrorMessage error={updateConfigError} />}
      </CardBody>
    </Card>
  );
};
